import {PhoneHQConfirmDialog} from "ch3-ui-lib";
import {useDispatch} from "react-redux";
import {useNavigate} from "react-router-dom";
import {showToast} from "../../store/features/Notifications/notificationsSlice.tsx";
import {
    useDeleteGoogleIntegrationMutation,
    useDeleteIntegrationMutation
} from "../../store/features/integrations/integrationsService.ts";
import {getGoogleIntegrationErrorMessage} from "./Google/googleIntegrationErrors.ts";

interface DisconnectIntegrationDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    integrationId?: number;
    platform: string;
}

/** "Disconnect integration?" for Google Workspace and Pipedrive cards. */
export const DisconnectIntegrationDialog = ({open, onOpenChange, integrationId, platform}: DisconnectIntegrationDialogProps) => {
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const [deleteIntegration, {isLoading: deleting}] = useDeleteIntegrationMutation();
    const [disconnectGoogle, {isLoading: disconnecting}] = useDeleteGoogleIntegrationMutation();

    const disconnect = async () => {
        if (!integrationId) return;
        try {
            if (platform === 'GOOGLE_WORKSPACE') {
                await disconnectGoogle({integrationId}).unwrap();
                onOpenChange(false);
                navigate('/admin/integrations/google/disconnected');
            } else if (platform === 'PIPEDRIVE') {
                await deleteIntegration({integrationId}).unwrap();
                onOpenChange(false);
                navigate('/admin/integrations/pipedrive/disconnected');
            }
        } catch (error: unknown) {
            const fallback = platform === 'GOOGLE_WORKSPACE'
                ? 'We could not disconnect Google Workspace. Try again, or contact PhoneHQ Support if the problem continues.'
                : 'Failed to disconnect integration.';
            dispatch(showToast({
                title: "Error",
                description: platform === 'GOOGLE_WORKSPACE' ? getGoogleIntegrationErrorMessage(error, fallback) : fallback,
                type: "error",
            }));
        }
    };

    return (
        <PhoneHQConfirmDialog
            open={open}
            onOpenChange={onOpenChange}
            title="Disconnect integration?"
            cancelLabel="Cancel"
            confirmLabel="Yes, disconnect"
            tone="danger"
            busy={deleting || disconnecting}
            onConfirm={disconnect}
        >
            By disconnecting this integration, you will lose all the data and this action can't be undone.
        </PhoneHQConfirmDialog>
    );
};
